import {
  EventType, HermesError, Transcript, mediaRefsIn, mergeInfo, obj, parseSessionInfo, resolvedStoredID, str,
  type GatewayEvent, type ServerRequest, type ServerRequestReply, type SessionInfo, type SessionSnapshot,
  type SlashExecResult, type TranscriptChange,
} from "@timibot/core";
import { mediaCache } from "./media";
import { Observable } from "./observable";
import { TranscriptCache } from "./persistence";
import type { AppModel } from "./app";

export interface QueuedImage {
  id: string;
  name: string;
  /** 本地预览（object URL） */
  thumbnail: string;
  /** 网关返回的暂存路径（用于撤回） */
  serverPath?: string;
  uploading: boolean;
}

/** 待发送的文件（PDF、Word、表格…） */
export interface QueuedFile {
  id: string;
  name: string;
  size: number;
  /** 写进消息的 @file: 引用（上传完成后才有） */
  refText?: string;
  uploading: boolean;
}

let chatCounter = 0;

/**
 * 一个对话（对应 Hermes 的一个会话，iOS 的 ChatStore）。
 * - 新对话在第一次发送时才调用 session.create，避免产生空会话
 * - 打开历史对话用 session.resume（网关里还活着就直接复用）
 * - 断线重连由 HermesClient 负责重绑；无法补齐时收到 `timibot.resync`，这里按存储 id 重新 resume
 */
export class ChatStore extends Observable {
  readonly id = `chat-${++chatCounter}`;
  liveSessionID?: string;
  storedSessionID?: string;
  transcript = new Transcript();
  info: SessionInfo = {};
  title?: string;
  /** 等待用户回答的服务端请求（确认执行、澄清问题、sudo 密码等） */
  pendingRequests: ServerRequest[] = [];
  images: QueuedImage[] = [];
  files: QueuedFile[] = [];
  draft = "";
  isLoading = false;
  isSending = false;
  lastActivity = Date.now();

  private loadTask?: Promise<void>;
  private pendingDeltas: GatewayEvent[] = [];
  private flushTimer?: ReturnType<typeof setTimeout>;

  constructor(readonly app: AppModel, readonly profile: string | undefined, storedSessionID?: string, title?: string) {
    super();
    this.storedSessionID = storedSessionID;
    this.title = title;
    // 先显示本机缓存的上次内容，连上服务器后 resume 会用服务器的版本替换
    if (storedSessionID) {
      const cached = TranscriptCache.load(storedSessionID);
      if (cached) {
        this.transcript.load(cached.messages);
        if (this.title === undefined) this.title = cached.title;
      }
    }
  }

  get api() { return this.app.api; }
  get isRunning(): boolean { return this.transcript.isRunning; }
  get isEmpty(): boolean { return this.transcript.items.length === 0 && !this.liveSessionID && !this.storedSessionID; }

  get displayTitle(): string {
    if (this.title) return this.title;
    if (this.info.title) return this.info.title;
    return "新对话";
  }

  setDraft(text: string): void {
    this.draft = text;
    this.changedNow();
  }

  // MARK: 会话

  /** 打开对话的请求不跟着界面走：多处同时调用只发一次 */
  loadIfNeeded(): Promise<void> {
    if (this.liveSessionID || !this.storedSessionID) return Promise.resolve();
    if (this.loadTask) return this.loadTask;
    const stored = this.storedSessionID;
    this.loadTask = this.resume(stored).finally(() => { this.loadTask = undefined; });
    return this.loadTask;
  }

  async resume(storedID: string): Promise<void> {
    const api = this.api;
    if (!api) return;
    this.isLoading = true;
    this.changed();
    try {
      this.adopt(await api.resumeSession(storedID, this.profile));
    } catch (e) {
      // 冷启动时连接还没建立：连上后 AppModel.refreshAfterConnect 会再调一次 loadIfNeeded
      if (!(e instanceof HermesError && (e.kind === "notConnected" || e.kind === "cancelled"))) {
        this.transcript.appendNotice(`无法打开对话：${e instanceof Error ? e.message : String(e)}`, "error");
      }
    } finally {
      this.isLoading = false;
      this.changed();
    }
  }

  /** 重连后无法补齐事件：按存储 id 重建 */
  async resync(): Promise<void> {
    const old = this.liveSessionID;
    this.liveSessionID = undefined;
    if (old) this.app.client?.untrack(old);
    if (!this.storedSessionID) return;
    await this.resume(this.storedSessionID);
  }

  private adopt(snap: SessionSnapshot): void {
    this.liveSessionID = snap.session_id;
    this.storedSessionID = resolvedStoredID(snap) ?? this.storedSessionID;
    if (snap.info) this.info = mergeInfo(this.info, snap.info);
    const t = snap.title ?? snap.info?.title;
    if (t) this.title = t;
    this.pendingDeltas = [];
    clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
    this.transcript.load(snap.messages, snap.info?.running ?? false);
    if (this.storedSessionID) TranscriptCache.save(this.storedSessionID, this.title, snap.messages);
    this.app.client?.track(snap.session_id);
    this.app.claimRequests(this);
    this.changed();
  }

  async ensureSession(): Promise<string> {
    if (this.liveSessionID) return this.liveSessionID;
    if (this.storedSessionID) {
      await this.resume(this.storedSessionID);
      if (this.liveSessionID) return this.liveSessionID;
    }
    const api = this.api;
    if (!api) throw HermesError.notConnected();
    const snap = await api.createSession(this.profile);
    this.liveSessionID = snap.session_id;
    this.storedSessionID = resolvedStoredID(snap);
    if (snap.info) this.info = mergeInfo(this.info, snap.info);
    this.app.client?.track(snap.session_id);
    this.app.chatDidCreateSession(this);
    this.app.claimRequests(this);
    this.changed();
    return snap.session_id;
  }

  // MARK: 发送

  /** 发送。`override` 为空时取输入框草稿。 */
  async send(override?: string): Promise<void> {
    const text = (override ?? this.draft).trim();
    if ((!text && !this.images.length && !this.files.length) || this.isSending) return;
    if (this.images.some((x) => x.uploading) || this.files.some((x) => x.uploading)) {
      this.app.showToast("附件还在上传，请稍候");
      if (override !== undefined && !this.draft) { this.draft = override; this.changed(); }
      return;
    }
    this.draft = "";
    this.changed();
    if (text.startsWith("/") && !this.images.length && !this.files.length) {
      await this.runSlash(text);
      return;
    }
    // 图片用和服务器历史一样的 `@image:路径` 写法显示成缩略图；文件的 `@file:` 引用要真正发给 Hermes
    const imageRefs = this.images.flatMap((x) => (x.serverPath ? [`@image:${x.serverPath}`] : []));
    const fileRefs = this.files.flatMap((x) => (x.refText ? [x.refText] : []));
    for (const img of this.images) URL.revokeObjectURL(img.thumbnail);
    this.images = [];
    this.files = [];
    const prompt = [text || (fileRefs.length ? "请看附件" : "请看图片"), ...fileRefs].join("\n");
    const bubble = [text, ...fileRefs, ...imageRefs].filter(Boolean).join("\n");
    await this.submit(prompt, bubble);
  }

  /** 以某个角色开始这段对话：把角色设定作为第一条消息发出去（界面上只显示一行“🎭 角色：xxx”） */
  async startRole(name: string, prompt: string): Promise<void> {
    await this.submit(`[角色：${name}]\n${prompt}`, undefined, `🎭 角色：${name}`);
    this.title = name;
    this.changed();
    if (this.liveSessionID) { try { await this.api?.renameSession(this.liveSessionID, name); } catch { /* 标题不重要 */ } }
  }

  private async submit(text: string, bubble: string | undefined, label?: string): Promise<void> {
    this.isSending = true;
    if (label) this.transcript.appendUserLabel(label); else this.transcript.appendUser(bubble ?? text);
    this.changed();
    const wasRunning = this.transcript.isRunning;
    try {
      const sid = await this.ensureSession();
      this.transcript.markAwaitingReply();
      this.changed();
      const result = await this.api!.submitPrompt(sid, text);
      if (result.status === "queued" && wasRunning) this.transcript.appendNotice("当前回合还在进行，这条消息已排队");
      else if (result.status === "steered") this.transcript.appendNotice("已作为转向插入当前回合");
      this.lastActivity = Date.now();
    } catch (e) {
      if (!wasRunning) this.transcript.setRunning(false);
      this.transcript.appendNotice(`发送失败：${e instanceof Error ? e.message : String(e)}`, "error");
      if (!this.draft) this.draft = text;
    } finally {
      this.isSending = false;
      this.changed();
    }
  }

  async stop(): Promise<void> {
    if (!this.liveSessionID) return;
    try { await this.api?.interrupt(this.liveSessionID); } catch (e) {
      this.app.showToast(`停止失败：${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // MARK: 斜杠命令（与桌面端 command.dispatch 流程一致）

  async runSlash(raw: string, depth = 0): Promise<void> {
    const body = raw.slice(1);
    const space = body.search(/\s/);
    const name = space < 0 ? body : body.slice(0, space);
    const arg = space < 0 ? undefined : body.slice(space + 1).trim() || undefined;
    if (!name) return;
    if (depth === 0) { this.transcript.appendUser(raw); this.changed(); }
    const api = this.api;
    if (!api) return;
    try {
      const sid = await this.ensureSession();
      let result: SlashExecResult;
      try {
        result = await api.dispatchCommand(name, arg, sid, this.profile);
      } catch (e) {
        if (!(e instanceof HermesError && e.kind === "rpc")) throw e;
        result = await api.execSlash(sid, raw);
      }
      await this.handleDispatch(result, arg, depth);
    } catch (e) {
      this.transcript.appendNotice(`/${name} 执行失败：${e instanceof Error ? e.message : String(e)}`, "error");
    }
    this.changed();
  }

  private async handleDispatch(r: SlashExecResult, arg: string | undefined, depth: number): Promise<void> {
    switch (r.type) {
      case "alias":
        if (r.target && depth < 3) await this.runSlash(`/${r.target}${arg ? " " + arg : ""}`, depth + 1);
        return;
      case "send": case "skill": {
        const notice = r.notice?.trim();
        if (notice) this.transcript.appendNotice(notice);
        const message = r.message?.trim();
        if (!message || !this.liveSessionID || !this.api) return;
        this.transcript.markAwaitingReply();
        this.changed();
        try { await this.api.submitPrompt(this.liveSessionID, message); } catch (e) {
          this.transcript.setRunning(false);
          this.transcript.appendNotice(`发送失败：${e instanceof Error ? e.message : String(e)}`, "error");
        }
        return;
      }
      case "prefill":
        if (r.message) this.draft = r.message;
        return;
      default: {
        const text = [r.output, r.notice, r.warning, r.message].map((x) => x?.trim()).filter(Boolean).join("\n\n");
        this.transcript.appendNotice(text || "（无输出）");
      }
    }
  }

  // MARK: 附件

  async attachImage(data: Uint8Array, preview: string, filename: string): Promise<void> {
    const item: QueuedImage = { id: crypto.randomUUID(), name: filename, thumbnail: preview, uploading: true };
    this.images = [...this.images, item];
    this.changed();
    try {
      const sid = await this.ensureSession();
      const result = await this.api!.attachImage(sid, data, filename);
      if (!result.attached) {
        this.images = this.images.filter((x) => x.id !== item.id);
        this.app.showToast(result.message ?? "图片上传失败");
        return;
      }
      const path = result.path ?? result.name;
      if (result.path) mediaCache.seed(result.path, new Blob([data as Uint8Array<ArrayBuffer>], { type: guessImageMime(filename) }));
      this.images = this.images.map((x) => (x.id === item.id ? { ...x, uploading: false, serverPath: path } : x));
    } catch (e) {
      this.images = this.images.filter((x) => x.id !== item.id);
      this.app.showToast(`图片上传失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      this.changed();
    }
  }

  async attachFile(data: Uint8Array, filename: string, mime: string): Promise<void> {
    const item: QueuedFile = { id: crypto.randomUUID(), name: filename, size: data.length, uploading: true };
    this.files = [...this.files, item];
    this.changed();
    try {
      const sid = await this.ensureSession();
      const result = await this.api!.attachFile(sid, data, filename, mime);
      // 气泡里的文件卡片点开时直接用本机这份，不用再下载
      const ref = mediaRefsIn(result.refText)[0];
      if (ref) mediaCache.seed(ref.source, new Blob([data as Uint8Array<ArrayBuffer>], { type: mime }));
      this.files = this.files.map((x) => (x.id === item.id ? { ...x, uploading: false, refText: result.refText } : x));
    } catch (e) {
      this.files = this.files.filter((x) => x.id !== item.id);
      this.app.showToast(`文件上传失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      this.changed();
    }
  }

  removeFile(file: QueuedFile): void {
    this.files = this.files.filter((x) => x.id !== file.id);
    this.changed();
  }

  async removeImage(image: QueuedImage): Promise<void> {
    this.images = this.images.filter((x) => x.id !== image.id);
    URL.revokeObjectURL(image.thumbnail);
    this.changed();
    if (image.serverPath && this.liveSessionID) {
      try { await this.api?.detachImage(this.liveSessionID, image.serverPath); } catch { /* 下一轮不会带上它 */ }
    }
  }

  // MARK: 事件 & 服务端请求

  private static coalesced = new Set<string>([EventType.messageDelta, EventType.reasoningDelta, EventType.thinkingDelta]);

  /** 流式增量合并：每 60ms 最多刷新一次界面，避免长回复逐字重排 Markdown */
  private flushDeltas(): void {
    clearTimeout(this.flushTimer);
    this.flushTimer = undefined;
    if (!this.pendingDeltas.length) return;
    for (const e of this.pendingDeltas) this.transcript.apply(e);
    this.pendingDeltas = [];
    this.changed();
  }

  handle(event: GatewayEvent): TranscriptChange {
    this.lastActivity = Date.now();
    if (ChatStore.coalesced.has(event.type)) {
      this.pendingDeltas.push(event);
      if (!this.flushTimer) this.flushTimer = setTimeout(() => this.flushDeltas(), 60);
      return { type: "updated" };
    }
    this.flushDeltas();
    const p = obj(event.payload);
    switch (event.type) {
      case EventType.sessionInfo: {
        const update = parseSessionInfo(p);
        if (update) this.info = mergeInfo(this.info, update);
        if (this.info.title) this.title = this.info.title;
        this.changed();
        return { type: "updated" };
      }
      case EventType.sessionTitle: {
        const t = str(p?.title);
        if (t) { this.title = t; this.changed(); }
        return { type: "updated" };
      }
      case EventType.requestCancel: {
        const rid = str(p?.id);
        this.pendingRequests = this.pendingRequests.filter((r) => r.id !== rid);
        this.changed();
        return { type: "updated" };
      }
      case EventType.clientResync: case EventType.sessionReclaimed:
        void this.resync();
        return { type: "none" };
      default: {
        const change = this.transcript.apply(event);
        if (event.type === EventType.messageComplete) this.pendingRequests = [];
        if (change.type === "turnCompleted" && this.storedSessionID) {
          TranscriptCache.save(this.storedSessionID, this.title, this.transcript.historySnapshot());
        }
        if (change.type !== "none") this.changed();
        return change;
      }
    }
  }

  receive(request: ServerRequest): void {
    if (this.pendingRequests.some((r) => r.id === request.id)) return;
    this.pendingRequests = [...this.pendingRequests, request];
    this.changed();
  }

  resolve(request: ServerRequest, reply: ServerRequestReply): void {
    this.pendingRequests = this.pendingRequests.filter((r) => r.id !== request.id);
    this.app.client?.respond(request, reply);
    this.changed();
  }
}

function guessImageMime(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "png") return "image/png";
  if (ext === "gif") return "image/gif";
  if (ext === "webp") return "image/webp";
  return "image/jpeg";
}
